import { useCallback, useEffect, useState } from "react";
import { client } from "../lib/api";
import { useArchiveInvalidate } from "../lib/archive-events";

const TOKEN_KEY = "archive_member_token";

const _authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : null;
};

/**
 * useMemberDesk — fetches and exposes the Member Desk dashboard payload.
 * Returns: { desk, loading, error, refresh, trackView, bookmark, unbookmark }
 */
export const useMemberDesk = () => {
  const [desk, setDesk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const headers = _authHeader();
    if (!headers) {
      setDesk(null);
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const r = await client.get("/members/me/desk", { headers });
      setDesk(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load Member Desk");
      setDesk(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useArchiveInvalidate(["members", "topics"], refresh);

  const trackView = useCallback(async (slug) => {
    const headers = _authHeader();
    if (!headers || !slug) return;
    try { await client.post(`/members/me/track-view/${slug}`, null, { headers }); }
    catch { /* silent — view-tracking is best-effort */ }
  }, []);

  const bookmark = useCallback(async (slug) => {
    const headers = _authHeader();
    if (!headers || !slug) return false;
    try {
      await client.post(`/members/me/bookmarks/${slug}`, null, { headers });
      await refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const unbookmark = useCallback(async (slug) => {
    const headers = _authHeader();
    if (!headers || !slug) return false;
    try {
      await client.delete(`/members/me/bookmarks/${slug}`, { headers });
      await refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  return { desk, loading, error, refresh, trackView, bookmark, unbookmark };
};

/** Hook that fires track-view once per slug per mount. Safe for anonymous (no-op). */
export const useTrackDossierView = (slug) => {
  useEffect(() => {
    if (!slug) return;
    const headers = _authHeader();
    if (!headers) return;
    client.post(`/members/me/track-view/${slug}`, null, { headers })
      .catch(() => { /* silent */ });
  }, [slug]);
};
